// Timesheets (Planning/Timesheets.md) — pure rules shared by the Cloud
// Functions that write entries and the UI that decides what to offer. Times
// are raw UTC instants; rounding, minimums and meal-break handling belong to
// costing (standard-timekeeping.ts), never to what's stored here.
import type { Booking, GigState, TimesheetEntry, TimesheetEntryStatus } from './types.js';
import { dateKey } from './quote.js';

const HOUR_MS = 60 * 60 * 1000;

// Self clock-in opens this long before a linked call starts, and closes this
// long after it ends. Outside the window a reviewer adds a manual entry.
export const CLOCK_IN_EARLY_HRS = 1;
export const CLOCK_IN_LATE_HRS = 4;
// A single entry can't span more than this (guards typos / forgotten clock-outs).
export const MAX_ENTRY_HOURS = 24;
// An OPEN entry older than this is flagged "forgot to clock out".
export const STALE_OPEN_HOURS = 16;
export const MAX_BREAK_MINUTES = 240;

const TRANSITIONS: Record<TimesheetEntryStatus, TimesheetEntryStatus[]> = {
  OPEN: ['PENDING'],
  PENDING: ['APPROVED', 'REJECTED'],
  APPROVED: ['PENDING'], // manager+ reopen
  REJECTED: [],
};

export function isValidEntryTransition(from: TimesheetEntryStatus, to: TimesheetEntryStatus): boolean {
  return TRANSITIONS[from].includes(to);
}

// Gig states in which a person may clock themselves in/out.
const CLOCKABLE_GIG_STATES: GigState[] = ['DRAFT', 'QUOTED', 'BOOKED'];
// Gig states that freeze every entry, reviewer edits included.
const LOCKED_GIG_STATES: GigState[] = ['INVOICED', 'PAID'];

// Finalizing a payroll report also freezes timesheets (Planning/Payroll.md) —
// otherwise the stored report could drift from the entries behind it. The
// gig carries payrollState so callers don't need an extra read.
export type GigPayrollState = 'PROCESSED' | 'PAID' | null | undefined;

export function isGigLockedForTimesheets(gigState: GigState, payrollState?: GigPayrollState): boolean {
  return LOCKED_GIG_STATES.includes(gigState) || payrollState === 'PROCESSED' || payrollState === 'PAID';
}

export type ClockInDecision =
  | { ok: true; callId?: string }
  | { ok: false; reason: string };

export interface ClockInContext {
  booking: Pick<Booking, 'state' | 'callIds' | 'linkedCalls' | 'scopeDate'>;
  gigState: GigState;
  now: Date;
  // The caller's already-open entry, if any (one at a time per person).
  openEntry?: Pick<TimesheetEntry, 'id'>;
  // Required only to match a call-less booking's scopeDate to today.
  timeZone?: string;
  // Narrow to one call when the person picked it; otherwise the nearest
  // call inside the window is chosen.
  callId?: string;
}

export function canClockIn(ctx: ClockInContext): ClockInDecision {
  const { booking, gigState, now, openEntry, callId } = ctx;
  if (booking.state !== 'ACCEPTED' && booking.state !== 'BOOKED') {
    return { ok: false, reason: 'Only an accepted booking can be clocked in to.' };
  }
  if (!CLOCKABLE_GIG_STATES.includes(gigState)) {
    return { ok: false, reason: `This gig is ${gigState.toLowerCase()}; time can't be clocked.` };
  }
  if (openEntry) return { ok: false, reason: 'You are already clocked in.' };

  const calls = booking.linkedCalls ?? [];
  if (calls.length === 0) {
    if (callId) return { ok: false, reason: 'That call is not part of this booking.' };
    const tz = ctx.timeZone ?? 'UTC';
    if (booking.scopeDate && dateKey(booking.scopeDate, tz) === dateKey(now, tz)) return { ok: true };
    return { ok: false, reason: 'This booking has no scheduled call today; ask a coordinator to add your time.' };
  }

  const candidates = callId ? calls.filter(c => c.callId === callId) : calls;
  if (callId && candidates.length === 0) return { ok: false, reason: 'That call is not part of this booking.' };
  const inWindow = candidates.filter(c => isWithinClockInWindow(c, now));
  if (inWindow.length === 0) {
    return { ok: false, reason: `Clock-in opens ${CLOCK_IN_EARLY_HRS}h before the call; ask a coordinator to add your time.` };
  }
  // Nearest start to now wins when windows overlap (back-to-back calls).
  inWindow.sort(
    (a, b) => Math.abs(a.startTime.getTime() - now.getTime()) - Math.abs(b.startTime.getTime() - now.getTime()),
  );
  return { ok: true, callId: inWindow[0].callId };
}

export function isWithinClockInWindow(call: { startTime: Date; endTime: Date }, now: Date): boolean {
  const t = now.getTime();
  return t >= call.startTime.getTime() - CLOCK_IN_EARLY_HRS * HOUR_MS
    && t <= call.endTime.getTime() + CLOCK_IN_LATE_HRS * HOUR_MS;
}

// Elapsed hours of an entry; an OPEN one runs to `now`.
export function entryHours(entry: Pick<TimesheetEntry, 'clockIn' | 'clockOut'>, now?: Date): number {
  const end = entry.clockOut ?? now;
  if (!end) return 0;
  return Math.max(0, (end.getTime() - entry.clockIn.getTime()) / HOUR_MS);
}

// Half-open interval overlap; an OPEN entry is treated as running to `now`,
// so back-to-back entries (one ends exactly when the next starts) don't clash.
export function entriesOverlap(
  a: Pick<TimesheetEntry, 'clockIn' | 'clockOut'>,
  b: Pick<TimesheetEntry, 'clockIn' | 'clockOut'>,
  now: Date,
): boolean {
  const aEnd = (a.clockOut ?? now).getTime();
  const bEnd = (b.clockOut ?? now).getTime();
  return a.clockIn.getTime() < bEnd && b.clockIn.getTime() < aEnd;
}

export function needsAttention(entry: Pick<TimesheetEntry, 'status' | 'clockIn'>, now: Date): boolean {
  return entry.status === 'OPEN' && now.getTime() - entry.clockIn.getTime() > STALE_OPEN_HOURS * HOUR_MS;
}

// Validates reviewer-supplied times (manual entry or edit). Returns an error
// message, or null when valid. `now` bounds the future check.
export function validateEntryTimes(
  input: { clockIn: Date; clockOut: Date; breakMinutes?: number },
  now: Date,
): string | null {
  const { clockIn, clockOut, breakMinutes } = input;
  if (Number.isNaN(clockIn.getTime()) || Number.isNaN(clockOut.getTime())) return 'Invalid date.';
  if (clockOut.getTime() <= clockIn.getTime()) return 'Clock-out must be after clock-in.';
  if (clockOut.getTime() > now.getTime()) return 'Times cannot be in the future.';
  const hours = (clockOut.getTime() - clockIn.getTime()) / HOUR_MS;
  if (hours > MAX_ENTRY_HOURS) return `An entry cannot span more than ${MAX_ENTRY_HOURS} hours.`;
  if (breakMinutes !== undefined) {
    if (!Number.isInteger(breakMinutes) || breakMinutes < 0) return 'Break must be a whole number of minutes.';
    if (breakMinutes > MAX_BREAK_MINUTES || breakMinutes >= hours * 60) return 'Break is longer than the entry allows.';
  }
  return null;
}
